import React, { useState, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Modal, Input, Select, message } from 'antd';
import BigNumber from 'bignumber.js';
import cs from './index.module.scss';
import { useStore } from '@/models';
import { formatAssetsPrecision } from '@/utils/utils';
import CoinDropdown from '@/components/coinDropdown';
import SelectArrowIcon from '@/components/icons/SelectArrowIcon';
import { BALANCETYPE } from '@/config/const';
import CloseFilledIcon from '@/components/icons/CloseFilledIcon';
import huazhuanImg from '@/assets/img/asset/huazhuan_2x.png';
import receiveFailIcon from '@/assets/img/welfare/receive-fail.png';

const { Option } = Select;
const PERCENTAGES = [0.1, 0.25, 0.5, 0.75, 1];

interface Props {
  symbol?: string;
}

const TransferModal: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { accountAsset, coin } = useStore();
  const [currentCoin, setCurrentCoin] = useState('');
  const [amount, setAmount] = useState<string>('');
  const {
    setTransferModal,
    showTransferModal,
    spotBalance,
    otcBalance,
    tigerSpuerBalance,
    fetchSupportAccount,
    supportAccount,
    fetchSupportCoin,
    supportCoinList,
    fetchTransfer,
    fetchBalance,
    tigerBalanceProfitTransfer,
    copyBalanceProfitTransfer,
  } = accountAsset;
  const { coinsPrecisionList } = coin;

  const [valueFrom, setValueFrom] = useState('');
  const [valueTo, setValueTo] = useState('');
  const [welfareModal, setWelfareModal] = useState<
    null | 'TRANSFER_WELFARE_RECYCLE_CONFIRM_REQUIRED' | 'TRANSFER_WELFARE_HAS_POSITION_OR_ORDER'
  >(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  useEffect(() => {
    if (supportAccount && supportAccount?.length > 0) {
      const fistItem = supportAccount[1];
      setValueFrom(fistItem);
      setValueTo(supportAccount.filter(i => i !== fistItem)[0]);
    }
  }, [supportAccount]);

  const findIntersection = (arr1, arr2) => {
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    const intersection = [...set1].filter(item => set2.has(item));
    return intersection.map(item => ({ coin: item }));
  };

  const supportCoinListFilter = useMemo(() => {
    if (supportCoinList && supportCoinList?.length > 0 && valueFrom && valueTo) {
      const arr1 = supportCoinList.find(item => item.userWalletType === valueFrom);
      const arr2 = supportCoinList.find(item => item.userWalletType === valueTo);
      const arr = findIntersection(arr1.coins, arr2.coins);
      setCurrentCoin(arr[0].coin);
      return arr;
    }
  }, [supportCoinList, valueFrom, valueTo]);

  useEffect(() => {
    fetchSupportCoin();
    fetchSupportAccount();
  }, []);

  useEffect(() => {
    if (showTransferModal) {
      fetchBalance({ type: BALANCETYPE });
      setAmount('');
    }
  }, [showTransferModal]);

  const fromList = useMemo(() => supportAccount.filter(item => item !== valueTo), [supportAccount, valueTo]);
  const toList = useMemo(() => supportAccount.filter(item => item !== valueFrom), [supportAccount, valueFrom]);

  const handleSelectTo = e => {
    setValueTo(e);
  };
  const handleSelectFrom = e => {
    setValueFrom(e);
  };

  const btnDisabled = useMemo(() => {
    if (amount && currentCoin && valueFrom && valueTo) {
      return true;
    }
    return false;
  }, [amount, currentCoin, valueFrom, valueTo]);

  const handleOk = async (confirmRecycleWelfare = false) => {
    if (!btnDisabled || transferSubmitting) return;
    const data = {
      amount,
      coin: currentCoin,
      fromWalletType: valueFrom.toLowerCase(),
      toWalletType: valueTo.toLowerCase(),
      ...(confirmRecycleWelfare ? { confirmRecycleWelfare: true } : {}),
    };
    setTransferSubmitting(true);
    try {
      const result = await fetchTransfer(data);
      if (result?.ok) {
        setWelfareModal(null);
        return;
      }
      const code = result?.welfareCode;
      if (code === 'TRANSFER_WELFARE_RECYCLE_CONFIRM_REQUIRED' || code === 'TRANSFER_WELFARE_HAS_POSITION_OR_ORDER') {
        setWelfareModal(code);
        return;
      }
      if (code === 'TRANSFER_WELFARE_RECYCLE_PROCESSING' || code === 'TRANSFER_WELFARE_RECYCLE_FAILED') {
        message.error(t(`welfareCenter.transfer.error.${code}`));
      }
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleCancel = () => {
    setAmount('');
    setTransferModal(false);
  };

  const balance = useMemo(() => {
    if (!showTransferModal || !currentCoin) return 0;
    const balanceMap: Record<string, any[]> = {
      OTC: otcBalance,
      SPOT: spotBalance,
      CONTRACT: tigerBalanceProfitTransfer,
      SUPER: tigerSpuerBalance,
      COPY: copyBalanceProfitTransfer,
    };
    const balanceList = balanceMap[valueFrom] || [];
    const res = balanceList.find(item => item.coin.toUpperCase() === currentCoin.toUpperCase());
    if (!res) return 0;
    const useRealTime = valueFrom === 'CONTRACT' || valueFrom === 'COPY';
    const val = useRealTime ? res.availableBalanceProfit : res.availableBalance;
    return formatAssetsPrecision(val, coinsPrecisionList[currentCoin]);
  }, [
    currentCoin,
    spotBalance,
    valueFrom,
    coinsPrecisionList,
    otcBalance,
    tigerBalanceProfitTransfer,
    copyBalanceProfitTransfer,
    tigerSpuerBalance,
    showTransferModal,
  ]);

  const setAmountByRatio = (ratio: number) => {
    if (!balance) return;
    const precision = coinsPrecisionList[currentCoin?.toUpperCase()] || 4;
    const next = new BigNumber(balance).multipliedBy(ratio).toFixed(precision, BigNumber.ROUND_DOWN);
    setAmount(new BigNumber(next).toFixed());
  };

  const suffixAll = (
    <div className={cs.all}>
      <span className={cs.coinUnit}>{currentCoin}</span>
      <button type="button" className={cs.allBtn} onClick={() => setAmountByRatio(1)}>
        {t('btn.all')}
      </button>
    </div>
  );

  const handleDrection = () => {
    setValueFrom(valueTo);
    setValueTo(valueFrom);
  };

  const handleSelect = v => {
    setCurrentCoin(v);
  };

  return (
    <>
      <Modal
        footer={null}
        title={t('common.transfer')}
        open={showTransferModal}
        onOk={() => handleOk()}
        onCancel={handleCancel}
        width="448px"
        keyboard={false}
        maskClosable={false}
        centered
        closeIcon={<CloseFilledIcon />}
        className={cs.transferModal}
      >
        <div className={cs.form}>
          <div className={cs.formBody}>
            <div className={cs.toFrom}>
              <div className={cs.to}>
                <span className={cs.title}>{t('transfer.from')}</span>
                <Select
                  popupClassName="normalDropdown"
                  placeholder={t('otc.publish.qxzzh')}
                  style={{ width: '100%', height: '44px' }}
                  optionLabelProp="label"
                  className={cx('selectBorder', cs.fieldSelect)}
                  dropdownMatchSelectWidth={false}
                  onSelect={handleSelectFrom}
                  suffixIcon={<SelectArrowIcon className="selece_icon" />}
                  value={valueFrom}
                >
                  {fromList?.map((_: any) => (
                    <Option value={_} key={_} label={t(_)} className="option">
                      {t(_)}
                    </Option>
                  ))}
                </Select>
              </div>
              <img src={huazhuanImg} alt="" className={cs.huazhuan} onClick={handleDrection} />
              <div className={cs.from}>
                <span className={cs.title}>{t('transfer.to')}</span>
                <Select
                  popupClassName="normalDropdown"
                  placeholder={t('otc.publish.qxzzh')}
                  style={{ width: '100%', height: '44px' }}
                  optionLabelProp="label"
                  className={cx('selectBorder', cs.fieldSelect)}
                  dropdownMatchSelectWidth={false}
                  onSelect={handleSelectTo}
                  suffixIcon={<SelectArrowIcon className="selece_icon" />}
                  value={valueTo}
                >
                  {toList?.map((_: any) => (
                    <Option value={_} key={_} label={t(_)} className="option">
                      {t(_)}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            <div className={cs.selectCoin}>
              <span className={cs.title}>{t('transfer.coin')}</span>
              <div className={cs.coinField}>
                <CoinDropdown handleSelect={handleSelect} coinList={supportCoinListFilter} isBorder />
              </div>
            </div>

            <div className={cs.ipt}>
              <div className={cs.amountHeader}>
                <span className={cs.title}>{t('common.amount')}</span>
                <span className={cs.balanceInline}>
                  {t('transfer.balance')}
                  <b>{balance}</b>
                  {currentCoin}
                </span>
              </div>
              <Input
                className={cx('iptBorderAddonAfter', cs.amountInput)}
                placeholder={t('transfer.amount')}
                onChange={e => {
                  const val = e.target.value;
                  const precision = coinsPrecisionList[currentCoin?.toUpperCase()] || 4;
                  const reg = new RegExp(`^\\d{0,10}(\\.\\d{0,${precision}})?$`);
                  if (val === '' || reg.test(val)) {
                    setAmount(val);
                  }
                }}
                addonAfter={suffixAll}
                value={amount}
                style={{ height: '60px' }}
              />
              <div className={cs.percentageBox}>
                {PERCENTAGES.map(item => (
                  <button
                    type="button"
                    key={item}
                    className={cs.percentageItem}
                    onClick={() => setAmountByRatio(item)}
                  >
                    {item * 100}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={cs.formFooter}>
            <div
              onClick={() => handleOk()}
              className={cx(btnDisabled ? 'primaryBtn' : 'primaryBtnDisabled', cs.modal_btn)}
            >
              {t('transfer.comfirm')}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={welfareModal === 'TRANSFER_WELFARE_RECYCLE_CONFIRM_REQUIRED'}
        title={t('common.tip')}
        footer={null}
        centered
        width={480}
        maskClosable={!transferSubmitting}
        closeIcon={<CloseFilledIcon />}
        className={cs.welfareConfirmModal}
        onCancel={() => {
          if (transferSubmitting) return;
          setWelfareModal(null);
        }}
      >
        <div className={cs.welfareConfirmDialog}>
          <div className={cs.welfareConfirmBody}>
            <h3>{t('welfareCenter.transfer.confirm.title')}</h3>
            <p>{t('welfareCenter.transfer.confirm.content')}</p>
          </div>
          <div className={cs.welfareConfirmFooter}>
            <button type="button" className={cs.secondary} disabled={transferSubmitting} onClick={() => setWelfareModal(null)}>
              {t('welfareCenter.think_again')}
            </button>
            <button type="button" disabled={transferSubmitting} onClick={() => handleOk(true)}>
              {t('btn.ok')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={welfareModal === 'TRANSFER_WELFARE_HAS_POSITION_OR_ORDER'}
        footer={null}
        centered
        width={480}
        closable
        closeIcon={<CloseFilledIcon />}
        className={cs.welfareBlockedModal}
        onCancel={() => setWelfareModal(null)}
      >
        <div className={cs.welfareBlockedDialog}>
          <img className={cs.welfareBlockedIcon} src={receiveFailIcon} alt="" />
          <h3>{t('welfareCenter.transfer.blocked.title')}</h3>
          <p>{t('welfareCenter.transfer.blocked.content')}</p>
          <button
            type="button"
            className={cs.welfareBlockedAction}
            onClick={() => {
              setWelfareModal(null);
              window.location.href = '/futures/contract/u_based/btc_usdt';
            }}
          >
            {t('welfareCenter.go_trade')}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default observer(TransferModal);
